/**
 * Router shell.
 *
 * Layout:
 *   ┌────────────────────────────────────────────────────────────┐
 *   │  Command Center                              user@email   │  ← chrome
 *   ├────────────────────────────────────────────────────────────┤
 *   │  [Operations]  Client Success  Engineering  …              │  ← component nav
 *   ├────────────────────────────────────────────────────────────┤
 *   │   <route content>                                          │
 *   └────────────────────────────────────────────────────────────┘
 *
 * The app authenticates, renders the chrome, and lands on the Dashboard: the
 * operator's queue of active to-dos. `/` and `/dashboard` both land on it; the
 * Dashboard tab is the first sidebar slot. The Operations stat-card row
 * (OperationsSummaryRow) is the navigation for the 8 lifecycle books and
 * renders only on a book path (OPERATIONS_BOOK_PATHS); Surface and Coverage are
 * sidebar sublinks with no book row. Surfaces are built from Chris's actual
 * process — each adds its route here.
 *
 * Authentication: the chrome consumes useMe(); a 401
 * (UnauthenticatedError via the `throwOnError` config) renders
 * <SignInPage /> instead of the chrome+nav.
 */

import { useEffect, useRef } from "react";
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  Link,
  redirect,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { useMe } from "@/lib/use-me";
import { UnauthenticatedError } from "@/lib/api";
import { useLogout } from "@/lib/use-logout";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { lfLogo } from "@/assets/lf-logo";
import { OperationsSummaryRow } from "@/components/operations-summary-row";
import { OPERATIONS_PHASES } from "@/lib/operations-phases";
import { GlobalSearch } from "@/components/global-search";
import { SurfacePage } from "@/routes/operations/surface";
import { IntakeVerificationPage } from "@/routes/operations/intake-verification";
import { UnderReviewPage } from "@/routes/operations/under-review";
import { AcceptedPage } from "@/routes/operations/accepted";
import { MessagesPage } from "@/routes/operations/messages";
import { PlacedPage } from "@/routes/operations/placed";
import { SettledPage } from "@/routes/operations/settled";
import { ClosedPage } from "@/routes/operations/closed";
import { DroppedPage } from "@/routes/operations/dropped";
import { RejectedPage } from "@/routes/operations/rejected";
import { FirmsPage } from "@/routes/client-success/firms";
import { ClientEmailsPage } from "@/routes/client-success/client-emails";
import { UsersPage } from "@/routes/client-success/users";
import { ClientSuccessSurfacePage } from "@/routes/client-success/surface";
import { IntakeGapsPage } from "@/routes/client-success/intake-gaps";
import { FirmSuccessPage } from "@/routes/client-success/firm-success/index";
import { FirmSuccessFirmPage } from "@/routes/client-success/firm-success/firm";
import { LeadVolumePage } from "@/routes/client-success/firm-success/lead-volume";
import { CoveragePage } from "@/routes/client-success/coverage";
import { DashboardPage } from "@/routes/dashboard";
import { AnnouncementsPage } from "@/routes/dashboard/announcements";
import { EngineeringPage } from "@/routes/engineering";
import { RegistryPage } from "@/routes/registry";
import { GlassPage } from "@/routes/glass";
import { GlassVocabularyPage } from "@/routes/glass/vocabulary";
import { GlassTruthPage } from "@/routes/glass/truth";
import { GlassMachinePage } from "@/routes/glass/machine";
import { GlassPulsePage } from "@/routes/glass/pulse";
import { GlassCatalogPage } from "@/routes/glass/catalog";
import { GlassStewardshipPage } from "@/routes/glass/stewardship";
import { GlassCommunicationsPage } from "@/routes/glass/communications";
import {
  DEPARTMENT_VIEW_LABEL,
  DepartmentWorkPage,
} from "@/routes/departments/work";
import { useDashboardCount } from "@/lib/use-dashboard";
import { SignInPage } from "@/routes/sign-in";

/**
 * Top-level component nav. Dashboard, Operations, and Client Success are
 * live; the other three are greyed. Home returns as a tab when Home is rebuilt
 * (last). Dashboard is first because it's the operator's landing — the queue of
 * active to-dos across components. Client Success (ADR-0077) is the second live
 * content component — the first step of pulling LF-admin tooling out of the Case
 * Portal into CC.
 *
 * `kind` discriminates how a row renders:
 *   - "link"     — a top-level destination (router-driven active highlight).
 *   - "header"   — a plain group label, not a link (the OPERATIONS group).
 *   - "sublink"  — an indented child link under a header; active highlight is
 *                  computed manually (see operationsLinkActive) because "The
 *                  System" must stay lit across all 8 book paths, which the
 *                  router's prefix-matching can't express from one `to`.
 *   - "disabled" — a greyed, not-yet-live slot.
 *
 * 2026-06-20 nav reorg: Operations became a GROUP — a plain OPERATIONS header
 * over Surface (the attention belt), The System (the 8 lifecycle books, reached
 * via the stat-card row), and Coverage (the routing-supply map, moved here out
 * of Client Success — Coverage is matter-movement substrate, not a firm
 * scorecard). A pure relocation; no book URLs, pages, or APIs changed.
 */
const COMPONENT_TABS = [
  {
    key: "dashboard",
    label: "Dashboard",
    kind: "link",
    path: "/dashboard",
    exact: false,
  },
  { key: "operations", label: "Operations", kind: "header" },
  {
    key: "operations-surface",
    label: "Surface",
    kind: "sublink",
    path: "/operations/surface",
  },
  {
    key: "operations-system",
    label: "The System",
    kind: "sublink",
    path: "/operations/intake-verification",
  },
  {
    key: "operations-coverage",
    label: "Coverage",
    kind: "sublink",
    path: "/operations/coverage",
  },
  {
    key: "operations-messages",
    label: "Messages",
    kind: "sublink",
    path: "/operations/messages",
  },
  {
    // The department view (#1229; design #1222 Q2): Operations' tasks,
    // tickets and issues. The label is Chris's three kinds, 2026-10-13.
    key: "operations-work",
    label: DEPARTMENT_VIEW_LABEL,
    kind: "sublink",
    path: "/operations/work",
  },
  {
    key: "firm-success",
    // Renamed from "Client Success" — the department is Firm Success in the
    // ratified vocabulary (2026-07-05; command-center slice). The URLs moved
    // to /firm-success at #1144 (2026-10-09; no translation layer, ruled
    // 2026-08-18); apps/worker/src/index.ts 301s the old page URLs.
    label: "Firm Success",
    kind: "link",
    path: "/firm-success",
    exact: false,
  },
  {
    key: "firm-success-work",
    label: DEPARTMENT_VIEW_LABEL,
    kind: "sublink",
    path: "/firm-success/work",
  },
  {
    key: "engineering",
    label: "Engineering",
    kind: "link",
    path: "/engineering",
    exact: false,
  },
  {
    key: "engineering-work",
    label: DEPARTMENT_VIEW_LABEL,
    kind: "sublink",
    path: "/engineering/work",
  },
  {
    key: "engineering-registry",
    label: "Registry",
    kind: "sublink",
    path: "/engineering/registry",
  },
  {
    // Nav name "The Glass" ruled by Chris 2026-08-30 at wave glass-1 open
    // (#790; THE GLASS phase, #786) — the ruled phase name, not a coinage.
    key: "engineering-glass",
    label: "The Glass",
    kind: "sublink",
    path: "/engineering/glass",
  },
  // Marketing, Finance and Legal land on their department view (#1229; design
  // #1222 Q2); the bare /marketing, /finance and /legal redirect there. The
  // last four have no other page: their link is their view. Labels are the
  // repo's own name.
  {
    key: "marketing",
    label: "Marketing",
    kind: "link",
    path: "/marketing/work",
    exact: false,
  },
  {
    key: "finance",
    label: "Finance",
    kind: "link",
    path: "/finance/work",
    exact: false,
  },
  {
    key: "legal",
    label: "Legal",
    kind: "link",
    path: "/legal/work",
    exact: false,
  },
  {
    key: "builder",
    label: "Builder",
    kind: "link",
    path: "/builder/work",
    exact: false,
  },
  {
    key: "data-mesh",
    label: "Data-Mesh",
    kind: "link",
    path: "/data-mesh/work",
    exact: false,
  },
  {
    key: "executive-assistant",
    label: "Executive-Assistant",
    kind: "link",
    path: "/executive-assistant/work",
    exact: false,
  },
  {
    key: "teacher",
    label: "Teacher",
    kind: "link",
    path: "/teacher/work",
    exact: false,
  },
] as const;

/**
 * The 8 book paths — every Operations lifecycle book (New Leads … Rejected),
 * i.e. OPERATIONS_PHASES minus the Surface (which is its own tab now). Derived
 * from the single source of truth in operations-phases.ts so adding a book is a
 * one-file change. Used for two things: gating the stat-card row (show it only
 * on a book) and lighting "The System" sidebar link across all 8 books.
 */
const OPERATIONS_BOOK_PATHS: readonly string[] = OPERATIONS_PHASES.filter(
  (p) => p.key !== "surface",
).map((p) => p.path);

/** Active-highlight test for the three OPERATIONS sub-links (manual, because
 *  "The System" spans 8 distinct book paths — see OPERATIONS_BOOK_PATHS). The
 *  bare `/operations` alias still loads the Surface page, so it lights Surface. */
function operationsLinkActive(key: string, pathname: string): boolean {
  switch (key) {
    case "operations-surface":
      return pathname === "/operations" || pathname === "/operations/surface";
    case "operations-system":
      return OPERATIONS_BOOK_PATHS.includes(pathname);
    case "operations-coverage":
      return pathname === "/operations/coverage";
    case "operations-messages":
      return pathname === "/operations/messages";
    case "operations-work":
      return pathname === "/operations/work";
    case "firm-success-work":
      return pathname === "/firm-success/work";
    case "engineering-work":
      return pathname === "/engineering/work";
    case "engineering-registry":
      return pathname === "/engineering/registry";
    case "engineering-glass":
      // The sub-routes (vocabulary, wave glass-2 #793) light the same sublink.
      return pathname.startsWith("/engineering/glass");
    default:
      return false;
  }
}

function Shell() {
  const me = useMe();
  // The stat-card row IS the Operations nav (session 21). It's Operations-only,
  // so scope it to Operations routes now that a second component (Client
  // Success, ADR-0077) is live — otherwise it would render above the Client
  // Success pages, which have their own sub-nav. This is the move the row's
  // historical-note comment below anticipated ("when other components light
  // up, the row moves into an Operations-only layout route"); a path check is
  // the minimal form of that while the route tree stays flat.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // The stat-card row is the navigation for the 8 lifecycle books, so it shows
  // ONLY on a book path — not on Surface or Coverage, which are their own tabs
  // with no book row (2026-06-20 nav reorg). A plain `startsWith("/operations")`
  // would wrongly paint the row over Surface and Coverage.
  const isOperationsBook = OPERATIONS_BOOK_PATHS.includes(pathname);

  const signedOut = me.error instanceof UnauthenticatedError;

  // #472 / #490 — a fresh sign-in lands on /dashboard (Tasks), not
  // whatever URL the auth wall interrupted. The sign-in ceremony only invalidates ["me"]; without this the
  // Shell re-renders the Outlet at the entry URL — so an operator arriving at a
  // bookmarked /operations/surface (or whose session expired while there) would
  // land back on the Surface. We watch the signed-out → signed-in transition and
  // route to the Dashboard exactly once. An already-authenticated operator
  // deep-linking to any page never trips this (signedOut was never true), so it
  // doesn't fight intentional navigation.
  const navigate = useNavigate();
  const wasSignedOutRef = useRef(false);
  useEffect(() => {
    if (signedOut) {
      wasSignedOutRef.current = true;
    } else if (me.data && wasSignedOutRef.current) {
      wasSignedOutRef.current = false;
      void navigate({ to: "/dashboard" });
    }
  }, [signedOut, me.data, navigate]);

  // A 401 means "render the signed-out view" — check this *before* the
  // loading branch. `["me"]` refetches on window focus (operators tab
  // between MC and their email to grab the OTP code mid-sign-in); during
  // that refetch `isLoading` flips true again because the query never
  // got data, but `me.error` is retained. Without this ordering the
  // Shell would flash "Loading…" and remount <SignInPage />, wiping the
  // in-progress sign-in step. See the OTP-tab-switch bug.
  if (signedOut) {
    return <SignInPage />;
  }

  if (me.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-plane-muted">Loading…</p>
      </div>
    );
  }

  if (me.isError || !me.data) {
    // Non-401 errors bubble to ErrorBoundary in main.tsx. This branch
    // shouldn't be reachable in practice given the throwOnError config,
    // but the explicit fallback keeps types honest.
    return <SignInPage />;
  }

  return (
    // Shell root is transparent — body paints the ground (bg-plane + the fixed
    // top-light radial, ADR-0138); text default is on-ground ink, inherited from
    // body. The navy sidebar below carries its own gradient + text-white.
    <div className="flex min-h-screen">
      {/* Navy sidebar — mirrors the Case Portal shell (portal.html:46,52): the
          components stack vertically and the active one carries an orange
          left-rule; brand header on top, signed-in user + sign-out at the
          bottom. */}
      <aside
        className="sticky top-0 h-screen self-start flex w-[220px] flex-shrink-0 flex-col text-white"
        style={{ background: "linear-gradient(180deg, #000527 0%, #000a3a 100%)" }}
      >
        <div className="flex items-center gap-2.5 border-b border-white/10 px-5 pb-5 pt-6">
          <img src={lfLogo} alt="LegalFlare" className="h-6 w-auto" />
          <div className="flex flex-col leading-tight">
            <span className="font-serif text-base text-white">Command Center</span>
            <span className="text-[10px] uppercase tracking-wider text-white/40">
              staff operating surface
            </span>
          </div>
        </div>

        <nav className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-2.5 py-4">
          {COMPONENT_TABS.map((tab) => {
            // Plain group label (the OPERATIONS header) — not a link.
            if (tab.kind === "header") {
              return (
                <div
                  key={tab.key}
                  className="px-3.5 pb-1 pt-4 text-[10px] font-semibold uppercase tracking-wider text-white/35"
                  data-testid={`component-group-${tab.key}`}
                >
                  {tab.label}
                </div>
              );
            }
            // Indented child link under a group header. Active is computed
            // manually (operationsLinkActive) so "The System" stays lit across
            // all 8 books — the router's single-`to` matching can't express that.
            if (tab.kind === "sublink") {
              const active = operationsLinkActive(tab.key, pathname);
              return (
                <Link
                  key={tab.key}
                  to={tab.path}
                  data-status={active ? "active" : undefined}
                  className={cn(
                    "rounded-md border-l-[3px] border-transparent py-2 pl-7 pr-3.5 text-[13px] font-medium text-white/50 transition-colors",
                    "hover:text-white/80",
                    "data-[status=active]:border-l-cta data-[status=active]:bg-cta/10 data-[status=active]:font-semibold data-[status=active]:text-white",
                  )}
                  data-testid={`component-tab-${tab.key}`}
                >
                  {tab.label}
                </Link>
              );
            }
            // Top-level live destination — router-driven active highlight.
            if (tab.kind === "link") {
              return (
                <Link
                  key={tab.key}
                  to={tab.path}
                  className={cn(
                    "rounded-md border-l-[3px] border-transparent px-3.5 py-2.5 text-[13px] font-medium text-white/50 transition-colors",
                    "hover:text-white/80",
                    "data-[status=active]:border-l-cta data-[status=active]:bg-cta/10 data-[status=active]:font-semibold data-[status=active]:text-white",
                  )}
                  activeOptions={{ exact: tab.exact }}
                  activeProps={{ "data-status": "active" }}
                  data-testid={`component-tab-${tab.key}`}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span>{tab.label}</span>
                    {tab.key === "dashboard" ? <DashboardBadge /> : null}
                  </span>
                </Link>
              );
            }
            // No other kinds today. Marketing / Finance / Legal became live
            // "link" tabs (2026-06-22, Dashboard Headlines model), so the prior
            // greyed "disabled" slot is unused and `tab` is `never` here. Re-add
            // a disabled branch (kind: "disabled") if a not-yet-live slot returns.
            return null;
          })}
        </nav>

        <div className="border-t border-white/10 px-5 py-4">
          <div className="text-[10px] uppercase tracking-wider text-white/35">
            Signed in as
          </div>
          <div
            className="truncate text-[13px] text-white/85"
            data-testid="user-email"
          >
            {me.data.email}
          </div>
          <div className="mt-1.5">
            <LogoutButton />
          </div>
        </div>
      </aside>

      {/* Main column — the stat-card row is the nav for the 8 lifecycle books
          (session 21: the prior dot+label sub-nav strip above this was removed
          because it duplicated the row; see the historical-note comment near
          `OPERATIONS_PHASES`). It's gated on `isOperationsBook` so it shows on a
          book path only — not on Surface or Coverage, which are their own tabs
          (2026-06-20 nav reorg). A full Operations layout route is the eventual
          home for it; the path gate is the minimal form while the route tree
          stays flat. */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Sticky top strip — the global "Find any case" search (#434) on every
            screen, with the Operations book row beneath it on /operations*. Pins
            via the document scroll (#436). Stacking band: page-body floating
            buttons (the per-card SurfaceTrigger and its kin in surface/accepted/
            placed/under-review) are `z-10`, so the strip MUST sit above them or
            a card's button rides over the tabs as it scrolls under — hence `z-30`,
            not `z-10`. Panels/dialogs/toasts are `z-50`+ and still overlay it. */}
        {/* Opaque ground chrome (ADR-0138): content scrolls under it, so it must
            be solid. `.ground-sheet` paints the same viewport-fixed radial as
            body, so the strip is seamless against the ground behind it. */}
        <div className="sticky top-0 z-30 ground-sheet">
          <GlobalSearch />
          {isOperationsBook ? <OperationsSummaryRow /> : null}
        </div>
        <main className="min-h-0 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * Sidebar count badge — the number of live to-dos that need this operator's
 * action (the Mine pane's mineCount). Polls every 30s plus on focus via
 * useDashboardCount. Hidden when the count is 0 or while loading — a
 * badge that says "0" is just noise.
 *
 * Slice K (ADR-0099): when some of those rows are unseen the badge splits to
 * "N · M new" — the answer to "is there something new?" without opening the
 * page. All seen → plain "N" (the pre-K form).
 */
function DashboardBadge() {
  const count = useDashboardCount();
  const value = count.data?.count ?? 0;
  const newCount = count.data?.newCount ?? 0;
  if (value <= 0) return null;
  return (
    <span
      data-testid="dashboard-count-badge"
      className="rounded-full bg-cta px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white"
    >
      {dashboardBadgeLabel(value, newCount)}
    </span>
  );
}

/** Badge text: "N · M new" while unseen rows exist, plain "N" once all seen.
 *  Exported for the formatting test (the badge itself renders inside the
 *  router singleton, which a unit test shouldn't have to mount). */
export function dashboardBadgeLabel(count: number, newCount: number): string {
  return newCount > 0 ? `${count} · ${newCount} new` : String(count);
}

/**
 * Logout button — small affordance in the chrome header. The mutation
 * hits POST /api/auth/logout, clears the query cache, and reloads the
 * page so the Shell rehydrates against the cleared session cookie
 * (the next useMe call returns 401, which renders SignInPage).
 */
function LogoutButton() {
  const logout = useLogout();
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => logout.mutate()}
      disabled={logout.isPending}
      data-testid="logout-button"
      className="h-auto px-0 py-0 text-[12px] font-normal text-white/55 hover:bg-transparent hover:text-white"
    >
      {logout.isPending ? "Signing out…" : "Sign out"}
    </Button>
  );
}

const rootRoute = createRootRoute({
  component: Shell,
});

// `/` redirects to `/dashboard` (Tasks — the canonical Dashboard path). The
// Shell's sign-in effect
// above only covers the signed-out → signed-in transition; this route-level
// redirect keeps a plain load with a live session landing on the same page.
//
// Slice F (2026-05-31): the Tasks routes carry an optional `?viewer=<email>`
// search param so the Mine/Peer toggle is deep-linkable and refresh-survives.
// Empty / whitespace-only is treated as absent (the GET routes already
// collapse the same way). `useViewerSelection` reads + writes this param.
// A legacy `/?viewer=` deep-link explicitly wanted the Tasks Peer view, so it
// redirects to `/dashboard` with the param intact.
type DashboardSearch = { viewer?: string };

function parseDashboardSearch(s: Record<string, unknown>): DashboardSearch {
  const v = s.viewer;
  if (typeof v !== "string") return {};
  const trimmed = v.trim();
  return trimmed === "" ? {} : { viewer: trimmed };
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  validateSearch: parseDashboardSearch,
  beforeLoad: ({ search }) => {
    if (search.viewer) {
      throw redirect({ to: "/dashboard", search: { viewer: search.viewer } });
    }
    throw redirect({ to: "/dashboard" });
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  component: DashboardPage,
  validateSearch: parseDashboardSearch,
});

// Dashboard → Announcements (ADR-0137). Announcements is the news — the operator
// announcements block, later the per-department signal headlines.
const dashboardAnnouncementsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/announcements",
  component: AnnouncementsPage,
});

const operationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operations",
  component: SurfacePage,
});

// The Surface — canonical path. The first Operations tab; reuses each book's
// card to show floated matters where they currently live.
const surfaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operations/surface",
  component: SurfacePage,
});

const intakeVerificationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operations/intake-verification",
  component: IntakeVerificationPage,
});

// Under Review book — the second rebuilt Operations surface (the routing-bloat
// view). Reachable via the Operations sub-nav.
const underReviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operations/under-review",
  component: UnderReviewPage,
});

// Accepted book — the bridge between the Under Review book and the Placed book: unsigned
// leads a firm has Accepted but not yet brought to Placed (awaiting the FSA).
const acceptedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operations/accepted",
  component: AcceptedPage,
});

// Placed book — the census of Placed: every case a Handling Firm
// has taken, bucketed into four age bands by time-in-Placed. Monitoring, not
// a worklist.
const placedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operations/placed",
  component: PlacedPage,
});

// Settled book — cases that have settled and are being
// walked through disbursement to Case Closed. The attention-heavy inverse of
// the Placed book — a worklist, not a census.
const settledRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operations/settled",
  component: SettledPage,
});

// The four books carried coined names in their URLs until #1142 (2026-10-16):
// /operations/whirlpool, /fishing, /holding-pen and /home-stretch. Each old
// path redirects to the book's label path, so a bookmark still lands.
const oldBookRoute = (oldPath: string, to: string) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path: oldPath,
    beforeLoad: () => {
      throw redirect({ to });
    },
  });
const oldUnderReviewRoute = oldBookRoute("/operations/whirlpool", "/operations/under-review");
const oldAcceptedRoute = oldBookRoute("/operations/fishing", "/operations/accepted");
const oldPlacedRoute = oldBookRoute("/operations/holding-pen", "/operations/placed");
const oldSettledRoute = oldBookRoute("/operations/home-stretch", "/operations/settled");

// Closed — the graveyard: the terminal book of cases that have reached Case
// Closed. The Settled-exit sink, so nothing vanishes off the end of the
// pipeline. A flat census of the dead, not a worklist.
const closedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operations/closed",
  component: ClosedPage,
});

// Dropped — the terminal drop book: cases a Handling Firm took to Placed and
// then dropped. Read-only paperwork tracking (drop letter + the deferred
// OF-notification), last in the lineup (after Closed) because drops are rare.
const droppedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operations/dropped",
  component: DroppedPage,
});

// Rejected — the terminal book of leads no Handling Firm accepted (routing
// exhausted, never placed). The largest book in the system, so it ships as a
// recent window (newest by Creation Date) rather than a flat render.
const rejectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operations/rejected",
  component: RejectedPage,
});

// ---------------------------------------------------------------------------
// Client Success (ADR-0077) — the second live component. Flat sub-nav:
// Surface · Firms · Users. `/firm-success` lands on Surface; `/firm-
// success/surface` is its canonical path. Surface's page is currently a
// placeholder while its content is being designed — the tab is the landing
// because that's where attention goes once the Surface is built, not because
// the page is built today. Each page renders its own <ClientSuccessNav>.
// ---------------------------------------------------------------------------
const clientSuccessIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/firm-success",
  component: ClientSuccessSurfacePage,
});

// Slice I (2026-06-01, ADR-0089 + ADR-0090): firms + users routes carry an
// optional `?firm=<id>` / `?user=<id>` search param so a Dashboard row's
// identity Link can deep-link straight into the detail panel. The page reads
// the param on mount via `useRouterState` and opens the panel; closing the
// panel writes the param away. URL is the source of truth for which panel
// is open — refresh-survives.
type FirmsSearch = { firm?: string };
type UsersSearch = { user?: string };

function parseFirmsSearch(s: Record<string, unknown>): FirmsSearch {
  const v = s.firm;
  if (typeof v !== "string") return {};
  const trimmed = v.trim();
  return trimmed === "" ? {} : { firm: trimmed };
}

function parseUsersSearch(s: Record<string, unknown>): UsersSearch {
  const v = s.user;
  if (typeof v !== "string") return {};
  const trimmed = v.trim();
  return trimmed === "" ? {} : { user: trimmed };
}

const clientSuccessFirmsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/firm-success/firms",
  component: FirmsPage,
  validateSearch: parseFirmsSearch,
});

const clientSuccessUsersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/firm-success/users",
  component: UsersPage,
  validateSearch: parseUsersSearch,
});

// Client emails (ADR-0115) — the per-firm review-and-approve subpage. First
// dynamic-segment route: `$id` is the firm record id. Reached from the
// dashboard review item or the Firm panel; the breadcrumb walks back to the
// roster. No search params.
const clientSuccessClientEmailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/firm-success/firms/$id/client-emails",
  component: ClientEmailsPage,
});

// Slice M (ADR-0100) — the Intake Gaps chase list. No search params; the
// page's firm rows deep-link OUT to the firms route's `?firm=` panel door.
const clientSuccessIntakeGapsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/firm-success/intake-gaps",
  component: IntakeGapsPage,
});

const clientSuccessSurfaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/firm-success/surface",
  component: ClientSuccessSurfacePage,
});

// Firm Success (#676) — the per-firm read surface over the metrics
// substrate. The lists page (OF/HF, sort modes, hidden + dormant sections)
// and each firm's page ($firmId — dynamic segment like client-emails).
// Open-card state is ephemeral view state, not a search param (the Coverage
// posture). Capacity IS a search param: clicking a firm in the handling list
// must open its handling page, and that intent has to survive the navigation.
const firmSuccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/firm-success/firm-success",
  component: FirmSuccessPage,
});

const firmSuccessFirmRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/firm-success/firm-success/$firmId",
  component: FirmSuccessFirmPage,
  validateSearch: (search: Record<string, unknown>): { cap?: "of" | "hf" } =>
    search.cap === "of" || search.cap === "hf" ? { cap: search.cap } : {},
});

// Lead Volume (#1051, Firm Success PRD Phase 2) — LegalFlare first, then each
// Originating Firm's full history on its own axis, with the change signals. No
// search params.
const leadVolumeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/firm-success/lead-volume",
  component: LeadVolumePage,
});

// Coverage (ADR-0101) — the supply-side choropleth. Lives under Operations
// (2026-06-20 nav reorg): Coverage is the routing substrate the auto-router
// reads to place matters, i.e. matter-movement, not a firm scorecard — so it
// belongs with Operations, not Client Success. Same page + component + API
// (`/api/firm-success/coverage`); only the route URL moved. No search params:
// chip selection and the pinned state are ephemeral view state.
const operationsCoverageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operations/coverage",
  component: CoveragePage,
});

// Messages (#542 slice 2) — the global firm-communications view: every Portal
// Messages thread in two lanes (case-tagged / firm-level), unread-first, with
// client-side search + compose. A non-book Operations sublink (like
// Notifications), so it lives in COMPONENT_TABS only, never in
// OPERATIONS_PHASES. No search params: search + filters are ephemeral view
// state (the Coverage posture).
const operationsMessagesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operations/messages",
  component: MessagesPage,
});

// Engineering (ADR-0109) — the GitHub-Issues-backed component. Read-first: the
// open lf2.0 issues from the cabinet repo, each row linking out to GitHub. No
// search params; the surface is system-wide.
const engineeringRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/engineering",
  component: EngineeringPage,
});

// Registry glass (ADR-0150, ADR-0151) — the metrics substrate's canon viewer,
// a sublink under Engineering. Read-only: the atoms render from the
// bundled registry.json; GET /api/registry adds the live ledger status strip.
// No role gating (Cloudflare Access at the edge, like every page).
const registryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/engineering/registry",
  component: RegistryPage,
});

// The Glass (wave glass-1, #790) — the semantic layer's front door, a sublink
// under Engineering. Read-only: is / does / how render from the build-packaged
// canon; GET /api/glass adds the freshness strip. No role gating (Cloudflare
// Access at the edge, like every page).
const glassRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/engineering/glass",
  component: GlassPage,
});

// The Glass — vocabulary pane (wave glass-2, #793): glossary + ruled-out +
// retired, rendered verbatim from the packaged canon. A sub-route so the
// front door stays one screen; same read-only posture, no role gating.
const glassVocabularyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/engineering/glass/vocabulary",
  component: GlassVocabularyPage,
});

// The Glass — truth pane (wave glass-3, #795): the register rendered verbatim
// from the packaged canon — every front file, verdict, checked_on date. A
// sub-route (605 rows at wave open would bury the front door); same read-only
// posture, no role gating.
const glassTruthRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/engineering/glass/truth",
  component: GlassTruthPage,
});

// The Glass — machine pane (wave glass-4, #800): the five actors, the trigger
// map, and every non-archived workflow's node flow, rendered read-only from
// the packaged canon (generated from the committed n8n exports — no live n8n
// read). A sub-route (nine workflow diagrams would bury the front door); same
// read-only posture, no role gating.
const glassMachineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/engineering/glass/machine",
  component: GlassMachinePage,
});

// The Glass — pulse pane (wave glass-5, #802): drift rendered — the last
// live-drift run and what moved, from the committed drift artifacts
// (engineering/contract/drift/<date>.yaml, written by live-drift-report.py
// --emit; no live read at render time). Same read-only
// posture, no role gating.
const glassPulseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/engineering/glass/pulse",
  component: GlassPulsePage,
});

// The Glass — catalog pane (wave glass-6, #805): catalog-lookup in the
// browser — client-side search over the committed catalog.generated.json,
// packaged verbatim and whole into the glass canon (already fed: the CI
// regenerate-and-compare is the feeder; no live read at render time). Same
// read-only posture, no role gating.
const glassCatalogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/engineering/glass/catalog",
  component: GlassCatalogPage,
});

// The Glass — stewardship pane (wave glass-7, #807): stamps and routes by
// their measured ratification marks (activeness.yaml + entrance.yaml packaged
// verbatim and whole; the mark derivation is the generator's, declared), plus
// the covered-vs-findable join and the new-document path pointer. Same
// read-only posture, no role gating.
const glassStewardshipRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/engineering/glass/stewardship",
  component: GlassStewardshipPage,
});

// The Glass — communications pane (#964): what LegalFlare sends to a Firm's
// Contact or a Potential Client, each card beside its committed example
// (activeness.yaml communications-roster packaged verbatim; the examples
// copied into public/ at build). Same read-only posture, no role gating.
const glassCommunicationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/engineering/glass/communications",
  component: GlassCommunicationsPage,
});

// The department views (#1229, I2 of the department-dashboard plan,
// engineering/plans/project-department-dashboard.yaml; design #1222 Q2, ruled
// #1225) — one page per department at
// /<key>/work, the key being the repo's suffix in lower case (DEPARTMENTS in
// packages/shared/src/departments.ts). One component renders all ten from
// GET /api/departments/:key. No search params.
function departmentWorkRoute<K extends string>(key: K) {
  return createRoute({
    getParentRoute: () => rootRoute,
    path: `/${key}/work` as `/${K}/work`,
    component: function DepartmentWorkRouteComponent() {
      return <DepartmentWorkPage departmentKey={key} />;
    },
  });
}

const builderWorkRoute = departmentWorkRoute("builder");
const dataMeshWorkRoute = departmentWorkRoute("data-mesh");
const engineeringWorkRoute = departmentWorkRoute("engineering");
const executiveAssistantWorkRoute = departmentWorkRoute("executive-assistant");
const financeWorkRoute = departmentWorkRoute("finance");
const firmSuccessWorkRoute = departmentWorkRoute("firm-success");
const legalWorkRoute = departmentWorkRoute("legal");
const marketingWorkRoute = departmentWorkRoute("marketing");
const operationsWorkRoute = departmentWorkRoute("operations");
const teacherWorkRoute = departmentWorkRoute("teacher");

// Marketing / Finance / Legal held "coming later" placeholder pages from
// 2026-06-22 until #1229; their bare routes now redirect to the department
// view, so a link already sent still lands.
const marketingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/marketing",
  beforeLoad: () => {
    throw redirect({ to: "/marketing/work" });
  },
});

const financeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/finance",
  beforeLoad: () => {
    throw redirect({ to: "/finance/work" });
  },
});

const legalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/legal",
  beforeLoad: () => {
    throw redirect({ to: "/legal/work" });
  },
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  dashboardRoute,
  dashboardAnnouncementsRoute,
  operationsRoute,
  surfaceRoute,
  intakeVerificationRoute,
  underReviewRoute,
  acceptedRoute,
  placedRoute,
  settledRoute,
  closedRoute,
  droppedRoute,
  rejectedRoute,
  operationsCoverageRoute,
  operationsMessagesRoute,
  clientSuccessIndexRoute,
  clientSuccessFirmsRoute,
  clientSuccessClientEmailsRoute,
  clientSuccessUsersRoute,
  clientSuccessIntakeGapsRoute,
  clientSuccessSurfaceRoute,
  firmSuccessRoute,
  firmSuccessFirmRoute,
  leadVolumeRoute,
  engineeringRoute,
  registryRoute,
  glassRoute,
  glassVocabularyRoute,
  glassTruthRoute,
  glassMachineRoute,
  glassPulseRoute,
  glassCatalogRoute,
  glassStewardshipRoute,
  glassCommunicationsRoute,
  marketingRoute,
  financeRoute,
  legalRoute,
  oldUnderReviewRoute,
  oldAcceptedRoute,
  oldPlacedRoute,
  oldSettledRoute,
  builderWorkRoute,
  dataMeshWorkRoute,
  engineeringWorkRoute,
  executiveAssistantWorkRoute,
  financeWorkRoute,
  firmSuccessWorkRoute,
  legalWorkRoute,
  marketingWorkRoute,
  operationsWorkRoute,
  teacherWorkRoute,
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
